import React from 'react';

/**
 * B · an admin-authored announcement — the sheet users see (design_handoff_app_notices
 * "ManualNotice"). The admin preview renders THIS component, so what is composed is what ships.
 *
 * `#0A0A0A` sheet, white text: a 150px hero (IMAGE = cover · COLOR = flat · PLATE = `#161616`
 * with the label set large in the accent, split on `·`, the first part on ONE line, never
 * wrapping), the label pill ringed in the accent + the round close, a sentence-case headline,
 * the body in grey, the primary pill in the accent (ink by luminance), NOT NOW and the footnote.
 * The container (grabber, radius, docking) is `AppNoticeShell tone="ink"` or the preview frame.
 * Dismissing starts a 7-day quiet period across all manual notices (server-side).
 */
export function luminance(hex) {
  const h = (hex || '#000').replace('#', '').padEnd(6, '0');
  const c = [0, 2, 4].map((i) => { const v = parseInt(h.slice(i, i + 2), 16) / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}
export const inkOn = (hex) => (luminance(hex) > 0.18 ? '#000' : '#fff');

/** The plate: `ANNOUNCEMENT · PENTICTON` → main `ANNOUNCEMENT` sized to fit one line, sub `PENTICTON`. */
export function plateOf(label) {
  const parts = (label || '').split(/\s*·\s*/);
  const main = (parts[0] || '').toUpperCase();
  const fs = Math.max(14, Math.min(30, Math.floor(340 / (Math.max(main.length, 1) * 0.69))));
  return { main, sub: parts.slice(1).join(' · ').toUpperCase(), fs };
}

export default function ManualNotice({ notice, onPrimary, onSecondary }) {
  const accent = notice.accent || '#22ECFB';
  const label = notice.eyebrow || '';
  const hero = () => {
    const base = { height: 150, borderRadius: 22, overflow: 'hidden', flexShrink: 0 };
    if (notice.heroKind === 'image' && notice.heroValue) return <div style={{ ...base, background: `#161616 url(${notice.heroValue}) center/cover no-repeat` }} aria-hidden="true" />;
    if (notice.heroKind === 'color') return <div style={{ ...base, background: notice.heroValue || accent }} aria-hidden="true" />;
    const plate = plateOf(notice.heroValue || label);
    return (
      <div className="flex items-center justify-center" style={{ ...base, padding: '0 18px', background: '#161616' }} aria-hidden="true">
        <div className="flex w-full min-w-0 flex-col items-center uppercase" style={{ gap: 10 }}>
          <span className="max-w-full truncate font-mono font-extrabold" style={{ fontSize: plate.fs, letterSpacing: '.08em', lineHeight: 1, color: accent }}>{plate.main}</span>
          {plate.sub && <span className="max-w-full truncate font-mono text-[11px] font-bold" style={{ letterSpacing: '.2em', color: '#8A8A8A' }}>{plate.sub}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col font-mono text-white" style={{ gap: 16, padding: '12px 18px 22px' }} data-testid="manual-notice">
      {hero()}
      <div className="flex items-center justify-between" style={{ gap: 12 }}>
        <span className="flex min-w-0 items-center truncate rounded-full text-[10px] font-extrabold uppercase" style={{ height: 30, padding: '0 14px', background: '#000', boxShadow: `inset 0 0 0 2px ${accent}`, letterSpacing: '.16em' }}>{label}</span>
        <button type="button" onClick={onSecondary} aria-label="Close" className="flex flex-shrink-0 items-center justify-center rounded-full text-white transition-colors hover:bg-[#2E2E2E]" style={{ width: 40, height: 40, background: '#1F1F1F' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" /></svg>
        </button>
      </div>
      <h2 className="m-0 text-[26px] font-bold normal-case" style={{ lineHeight: 1.15, letterSpacing: '-.01em', textWrap: 'pretty', textTransform: 'none' }}>{notice.headline}</h2>
      <p className="m-0 text-[14px]" style={{ lineHeight: 1.6, color: '#8A8A8A', textWrap: 'pretty' }}>{notice.body}</p>
      <button type="button" onClick={onPrimary} className="rounded-full text-[12px] font-extrabold uppercase transition-opacity hover:opacity-[.88]" style={{ height: 56, background: accent, color: inkOn(accent), letterSpacing: '.16em' }}>
        {notice.primaryLabel}
      </button>
      <div className="flex flex-col items-center" style={{ gap: 8 }}>
        <button type="button" onClick={onSecondary} className="rounded-full bg-transparent text-[11px] font-bold uppercase text-[#8A8A8A] transition-colors hover:text-white" style={{ height: 40, padding: '0 18px', letterSpacing: '.16em' }}>
          {notice.secondaryLabel || 'NOT NOW'}
        </button>
        <span className="text-center text-[10px] uppercase" style={{ letterSpacing: '.12em', color: '#6B6B6B' }}>You will not see this one again.</span>
      </div>
    </div>
  );
}
